import {decomposeColor, hslToRgb} from '@mui/material/styles'

// WCAG 2 minimums: body text, and large text or the edges of controls (fields, outlined buttons).
export const CONTRAST_TEXT = 4.5
export const CONTRAST_UI = 3

const BLACK = '#000000'
const WHITE = '#ffffff'
// Colours land on whole channels and alphas on three decimals; aiming a little above the minimum
// keeps that rounding from dropping a fitted colour just under it.
const ROUNDING_MARGIN = 0.03

interface IRgba {
	rgb: number[],
	alpha: number,
}

const parse = (color: string): IRgba => {
	const decomposed = decomposeColor(color.startsWith('hsl') ? hslToRgb(color) : color)
	const [r, g, b, alpha = 1] = decomposed.values
	return {rgb: [r, g, b], alpha}
}

const toCss = ({rgb, alpha}: IRgba) => {
	const channels = rgb.map((value) => Math.round(Math.min(255, Math.max(0, value))))
	return alpha >= 1
		? `#${channels.map((value) => value.toString(16).padStart(2, '0')).join('')}`
		: `rgba(${channels.join(', ')}, ${Math.round(alpha * 1000) / 1000})`
}

const towards = (rgb: number[], target: string, weight: number): IRgba => {
	const to = parse(target).rgb
	return {rgb: rgb.map((value, i) => value + (to[i] - value) * weight), alpha: 1}
}

// The smallest value in [from, 1] that passes; 1 itself is taken to pass.
const search = (passes: (value: number) => boolean, from = 0) => {
	let low = from
	let high = 1
	for (let i = 0; i < 20; i++) {
		const middle = (low + high) / 2
		if (passes(middle)) high = middle
		else low = middle
	}
	return high
}

const linear = (channel: number) => {
	const value = channel / 255
	return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
}

// WCAG relative luminance, unrounded (MUI's getLuminance keeps three decimals).
export const luminance = (color: string) => {
	const [r, g, b] = parse(color).rgb
	return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)
}

// A see-through colour painted over an opaque one, the way the browser shows it.
export const flatten = (color: string, background: string) => {
	const {rgb, alpha} = parse(color)
	const base = parse(background).rgb
	return toCss({rgb: rgb.map((value, i) => value * alpha + base[i] * (1 - alpha)), alpha: 1})
}

// WCAG contrast ratio, 1 to 21. The background must be opaque; the colour may be see-through.
export const contrastRatio = (color: string, background: string) => {
	const a = luminance(flatten(color, background))
	const b = luminance(background)
	return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

// The worst case over everything the colour may sit on, e.g. every stop of a gradient.
export const minContrast = (color: string, backgrounds: string[]) =>
	Math.min(...backgrounds.map((background) => contrastRatio(color, background)))

// The colour itself when it already reads on the backgrounds; otherwise the nearest one that does.
// A see-through colour first gets more solid, then any colour moves towards black or white,
// whichever end reads better. When even that end falls short, it is the best there is.
export const ensureContrast = (color: string, backgrounds: string[], min: number): string => {
	if (minContrast(color, backgrounds) >= min) return color

	const {rgb, alpha} = parse(color)
	if (alpha < 1) {
		const solid = toCss({rgb, alpha: 1})
		if (minContrast(solid, backgrounds) < min) return ensureContrast(solid, backgrounds, min)
		const aim = min + ROUNDING_MARGIN
		return toCss({rgb, alpha: search((value) => minContrast(toCss({rgb, alpha: value}), backgrounds) >= aim, alpha)})
	}

	const target = minContrast(BLACK, backgrounds) >= minContrast(WHITE, backgrounds) ? BLACK : WHITE
	const at = (weight: number) => toCss(towards(rgb, target, weight))
	if (minContrast(target, backgrounds) < min) return target
	const aim = Math.min(min + ROUNDING_MARGIN, minContrast(target, backgrounds))
	return at(search((weight) => minContrast(at(weight), backgrounds) >= aim))
}

// The colour to paint a surface with so that `text` reads on all of it: `shades` turns the colour
// into what actually gets painted, e.g. the stops of its gradient. The colour moves away from the
// text, darker under light text and lighter under dark, only as far as it takes.
export const fitBackground = (
	color: string,
	text: string,
	min: number,
	shades: (color: string) => string[] = (value) => [value],
): string => {
	if (minContrast(text, shades(color)) >= min) return color

	const fits = (value: string) => minContrast(text, shades(value)) >= min + ROUNDING_MARGIN
	const {rgb} = parse(color)
	const target = luminance(text) > 0.5 ? BLACK : WHITE
	const at = (weight: number) => toCss(towards(rgb, target, weight))
	return at(search((weight) => fits(at(weight))))
}
